import { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import { callBackend } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import { useOptionalCrm } from './CrmContext';

type NameInfo = { contactId: string; name: string; company?: string; vip: boolean };

/** Names already looked up, per customer and number; null = not a contact. */
const cache = new Map<string, NameInfo | null>();
const queued = new Map<string, Set<string>>();
const listeners = new Set<() => void>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function cacheKey(customerId: string, number: string) {
  return `${customerId}|${number}`;
}

/** The number in "Name<number>" or a bare number; '' for extensions and other short codes. */
export function lookupNumber(value: string | undefined) {
  const match = /<([^<>]*)>\s*$/.exec(value ?? '');
  const number = (match ? match[1] : value ?? '').trim();
  return number.replace(/[^\d+]/g, '').length >= 6 ? number : '';
}

function flush() {
  flushTimer = null;
  const batches = Array.from(queued.entries());
  queued.clear();
  for (const [customerId, numbers] of batches) {
    const list = Array.from(numbers);
    for (let index = 0; index < list.length; index += 200) {
      const chunk = list.slice(index, index + 200);
      void callBackend<Record<string, NameInfo>>(backendApi.crm.lookupNumbers, { numbers: chunk })
        .then((result) => {
          for (const number of chunk) cache.set(cacheKey(customerId, number), result[number] ?? null);
        })
        .catch(() => {
          for (const number of chunk) cache.set(cacheKey(customerId, number), null);
        })
        .finally(() => listeners.forEach((listener) => listener()));
    }
  }
}

/** Contact names for numbers shown on screen, looked up in batches; empty when caller names are off. */
export function useCallerNames(values: Array<string | undefined>) {
  const crm = useOptionalCrm();
  const customerId = crm?.data?.customerId;
  const enabled = Boolean(crm?.data?.features.callerNames);
  const [, setVersion] = useState(0);

  useEffect(() => {
    const listener = () => setVersion((version) => version + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const numbers = enabled && customerId ? Array.from(new Set(values.map(lookupNumber).filter(Boolean))) : [];
  const signature = numbers.join(',');

  useEffect(() => {
    if (!customerId || !signature) return;
    let added = false;
    for (const number of signature.split(',')) {
      if (cache.has(cacheKey(customerId, number))) continue;
      const set = queued.get(customerId) ?? new Set<string>();
      set.add(number);
      queued.set(customerId, set);
      added = true;
    }
    if (added && !flushTimer) flushTimer = setTimeout(flush, 60);
  }, [customerId, signature]);

  const names: Record<string, NameInfo> = {};
  if (customerId) {
    for (const value of values) {
      const number = lookupNumber(value);
      const info = number ? cache.get(cacheKey(customerId, number)) : null;
      if (value && info) names[value] = info;
    }
  }
  return names;
}

/**
 * A number as the contact's name (and company), with the number underneath; just the number when
 * it is not a contact or caller names are off. Clicking the name opens the contact.
 */
export function CallerName({ value, className = '', compact = false }: { value: string | undefined; className?: string; compact?: boolean }) {
  const names = useCallerNames([value]);
  const crm = useOptionalCrm();
  const info = value ? names[value] : undefined;
  if (!info) return <span className={className}>{value || '—'}</span>;

  const label = (
    <>
      {info.vip && <Star className="inline w-3 h-3 mr-0.5 -mt-0.5 text-amber-500 fill-amber-400" aria-label="VIP" />}
      {info.name}
      {info.company && info.company !== info.name && <span className="font-normal text-gray-500 dark:text-gray-400"> · {info.company}</span>}
    </>
  );
  return (
    <span className={`inline-flex flex-col min-w-0 ${className}`}>
      <button
        type="button"
        onClick={() => crm?.openContact(info.contactId)}
        className="text-left font-medium text-primary-700 dark:text-primary-300 hover:underline truncate"
        title="Open contact"
      >
        {label}
      </button>
      {!compact && <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{lookupNumber(value)}</span>}
    </span>
  );
}

/** Just the contact's name (opens the contact), or nothing: for tables that already show the number. */
export function ContactNameLine({ value }: { value: string | undefined }) {
  const names = useCallerNames([value]);
  const crm = useOptionalCrm();
  const info = value ? names[value] : undefined;
  if (!info) return null;
  return (
    <button
      type="button"
      onClick={() => crm?.openContact(info.contactId)}
      className="block max-w-full truncate text-left text-xs font-medium text-primary-700 dark:text-primary-300 hover:underline"
      title={info.company ? `${info.name} · ${info.company}` : info.name}
    >
      {info.vip && <Star className="inline w-3 h-3 mr-0.5 -mt-0.5 text-amber-500 fill-amber-400" aria-label="VIP" />}
      {info.name}
      {info.company && info.company !== info.name ? ` · ${info.company}` : ''}
    </button>
  );
}
